import React, { useEffect } from 'react';
import { useForm, Controller } from 'components/EnhancedReactHookForm';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  Icon,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Switch from 'components/Switch';
import TextField from 'components/TextField';
import Select from 'components/Select';
import DatePicker from 'components/DatePicker';
import Checkbox from 'components/Checkbox';
import { DEFAULT_DATE_FORMAT } from 'app/constants/DateTimeFormat';
import { isEmpty, trim } from 'lodash';
import { toMeridiemDateTimeString } from 'helpers/Date';
import { addMonths, addYears } from 'date-fns';
import clsx from 'clsx';
import ColorPicker from '../Calendar/ColorPicker/ColorPicker';
import { REPEAT_ENUM, REPEAT_TYPE, SCHEDULE_TAB_TYPE } from '../../constants';

const useStyles = makeStyles(() => ({
  doneLabel: {
    marginLeft: '6px',
    marginRight: '4px',
  },
}));

const EditScheduleDialog = (props) => {
  const { open, onClose, handleEditSchedule, schedule } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const { control, handleSubmit, errors, watch, setValue, reset } = useForm({
    defaultValues: {},
    mode: 'onBlur',
  });
  const LABEL_SIZE = 120;

  const isRepeat = watch('isEnableRepeat', schedule.isEnableRepeat);
  const repeatType = watch('repeatType', schedule.repeatType);

  useEffect(() => {
    if (!isRepeat) return;
    if (repeatType === REPEAT_ENUM.YEAR) {
      setValue('repeatEndDate', addYears(new Date(schedule?.scheduleDate), 1));
    } else {
      setValue('repeatEndDate', addMonths(new Date(schedule?.scheduleDate), 6));
    }
  }, [repeatType]);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  const handleSaveSchedule = (data) => {
    const _id = schedule?.id;
    const _data = {
      colour: schedule?.colour,
      scheduleDate: schedule?.scheduleDate,
      ...data,
    };
    handleEditSchedule(_data, _id);
    onClose();
    reset();
  };

  return (
    <Dialog open={open} onClose={onClose} scroll="paper" maxWidth="sm" fullWidth onKeyDown={(e) => e.stopPropagation()}>
      <DialogTitle className="flex justify-center">
        {t('dashboard:EDIT_DIALOG_SCHEDULE', { type: SCHEDULE_TAB_TYPE[schedule?.type]?.label })}
        {/* <Icon fontSize="default" className="ml-16">
          {scheduleType?.icon}
        </Icon> */}
      </DialogTitle>
      <DialogContent>
        <form noValidate>
          <Grid container direction="column" spacing={2} style={{ flexWrap: 'nowrap' }}>
            <Grid item>
              <Controller
                as={TextField}
                control={control}
                name="title"
                variant="outlined"
                label={t('dashboard:CALENDAR_TITLE')}
                labelSize={LABEL_SIZE}
                className="w-full"
                rules={{
                  validate: {
                    isRequired: (v) => trim(v) !== '' || t('VALIDATION_REQUIRED_MESSAGE'),
                  },
                }}
                error={Boolean(errors.title)}
                helperText={errors.title && errors.title.message}
                defaultValue={schedule?.title}
              />
            </Grid>
            <Grid item>
              <Controller
                as={TextField}
                control={control}
                name="detail"
                fullWidth
                variant="outlined"
                label={t('dashboard:CALENDAR_ADD_DETAILS')}
                labelSize={LABEL_SIZE}
                defaultValue={schedule?.detail}
                multiline
                rows={4}
                error={Boolean(errors.detail)}
                helperText={errors.detail && errors.detail.message}
              />
            </Grid>
            <Grid item>
              <IconButton disabled>
                <Icon>repeat_oulined</Icon>
              </IconButton>
              <span style={{ fontWeight: 'bold' }}>Repeat</span>
              <Controller
                as={Switch}
                control={control}
                name="isEnableRepeat"
                color="primary"
                defaultValue={schedule?.isEnableRepeat}
              />
            </Grid>
            <Grid item container direction="column" justify="flex-start" spacing={1} className="ml-32">
              <Grid item container xs={7} wrap="nowrap">
                <Grid item xs={6}>
                  <Controller
                    as={TextField}
                    control={control}
                    name="repeatInterval"
                    disabled={!isRepeat}
                    labelSize={80}
                    inline
                    rules={{
                      validate: {
                        isValidNumber: (v) =>
                          isEmpty(v) || /^([0-9]+)\.?([0-9]+)?$/g.test(v) || t('VALIDATION_NUMBER_INVALID_MESSAGE'),
                        isRequired: (v) => v !== '' || t('VALIDATION_REQUIRED_MESSAGE'),
                      },
                    }}
                    variant="outlined"
                    label="Every"
                    defaultValue={schedule?.repeatInterval ? schedule?.repeatInterval : 1}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    as={Select}
                    control={control}
                    name="repeatType"
                    className="w-full mr-8"
                    disabled={!isRepeat}
                    labelSize={5}
                    options={REPEAT_TYPE}
                    rules={{
                      validate: {
                        isRequired: (v) => v !== '' || t('VALIDATION_REQUIRED_MESSAGE'),
                      },
                    }}
                    variant="outlined"
                    inline
                    defaultValue={schedule?.repeatType ? schedule?.repeatType : REPEAT_ENUM.DAY}
                  />
                </Grid>
                {errors.repeatInterval && <FormHelperText error>{errors.repeatInterval.message}</FormHelperText>}
              </Grid>
              <Grid item xs={7}>
                <Controller
                  as={DatePicker}
                  control={control}
                  name="repeatEndDate"
                  label="End on"
                  disabled={!isRepeat}
                  inputVariant="outlined"
                  labelSize={80}
                  inline
                  format={DEFAULT_DATE_FORMAT}
                  defaultValue={schedule?.repeatEndDate ? schedule?.repeatEndDate : addMonths(new Date(), 6)}
                />
              </Grid>
            </Grid>
            <Grid item>
              <Controller
                as={<ColorPicker defaultColor={schedule?.colour} />}
                control={control}
                name="colour"
                defaultValue={schedule?.colour}
              />
            </Grid>
            <Grid item className="ml-8">
              <Controller
                as={Checkbox}
                control={control}
                name="isDone"
                className={clsx(classes.doneLabel)}
                inline
                label="Done"
                defaultValue={schedule?.isDone}
              />
            </Grid>
            {schedule?.lastUpdateDate && schedule?.lastUpdateUserFullname && (
              <Grid item className="ml-8">
                <Typography>
                  {`Last Updated: ${toMeridiemDateTimeString(schedule.lastUpdateDate)} ${
                    schedule.lastUpdateUserFullname || ''
                  }`}
                </Typography>
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button type="button" className={clsx('mr-8')} onClick={onClose}>
          {t('BUTTON_CANCEL')}
        </Button>
        <Button color="primary" variant="contained" type="button" onClick={handleSubmit(handleSaveSchedule)}>
          {t('dashboard:BUTTON_SAVE_CHANGE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditScheduleDialog;
